import Part from "@/components/body/parts/part";
import Utils, { Colors } from "@/components/shared/util";
import { Patient } from "@/store/modules/patient/types";
import _ from "lodash";
import SVG from "svg.js";
import { Store } from "vuex";
import { BodyConfig } from ".";

const MIN_OFFSET = 415;
const HEART_SHIFT_Y = -50;
const MAXIMUM_HEART_RATE_ANIMATION_TIME_MS = 700;
const ANIMATION_SCALE_START = 1.0;
const ANIMATION_SCALE_END = 1.1;
const BP_ANIMATION_STROKE_START = 8;
const BP_ANIMATION_STROKE_END = 16;
const VENOUS_ANIMATION_STROKE_START = 6;
const VENOUS_ANIMATION_STROKE_END = 12;

export default class Heart extends Part {
  clipRect!: SVG.Rect;
  heartAnimationGroup!: SVG.Element;
  bpLevel!: SVG.Element;
  venousLevel!: SVG.Element;
  heartClipVenousLevel!: SVG.Element;
  heartClipBpLevel!: SVG.Element;
  rate = 0;
  loopRunning = false;

  constructor(config: BodyConfig, container: SVG.Container, store: Store<any>) {
    super(config, container, store);

    this.svgGroup.move(0, HEART_SHIFT_Y);
  }

  getExtraData(patient: Patient) {
    this.extraData = {
      heartRate: patient.body.atoms.heartRate,
      bp: patient.body.atoms.bp,
      needData: _.get(patient, "body.atoms.needData.value") === 1
    };
  }

  get usingVolumeAtom() {
    return this.config.atomId === "volume";
  }

  updateData(patient: Patient | null) {
    super.updateData(patient);
    if (this.extraData.needData) {
      this.levelElements.forEach(element => {
        element.attr({ fill: Colors.Gray });
      });
    }

    if (!this.bpLevel) {
      this.bpLevel = SVG.select("#bpLevel", this.container.node).first();
    }
    if (!this.venousLevel) {
      this.venousLevel = SVG.select(
        "#venousLevel",
        this.container.node
      ).first();
    }

    if (this.globalConfig.strokeLinecapRound) {
      this.bpLevel.attr({ "stroke-linecap": "round" });
      this.venousLevel.attr({ "stroke-linecap": "round" });
    }

    this.bpLevel.stroke(
      Utils.getLevelColor(this.extraData.bp.level, this.showGreenGrayOnly)
    );

    // Only color venous level when the atom is volume
    if (this.atom && this.usingVolumeAtom && !this.extraData.needData) {
      this.venousLevel.stroke(
        Utils.getLevelColor(this.atom.level, this.showGreenGrayOnly)
      );
    } else {
      this.venousLevel.stroke(Colors.Gray);
    }

    if (!this.clipRect) {
      this.createClippingPath();
      this.heartAnimationGroup = SVG.select(
        "#heartAnimationGroup",
        this.container.node
      ).first();

      this.heartClipVenousLevel = SVG.select(
        "#heartClipVenousLevel",
        this.container.node
      ).first();

      this.heartClipBpLevel = SVG.select(
        "#heartClipBpLevel",
        this.container.node
      ).first();
    }

    this.rate =
      this.extraData.heartRate.value === 0
        ? 0
        : Math.floor((60 / this.extraData.heartRate.value) * 1000);

    // Animations must be enabled, loop hasn't already started, and a positive rate
    if (this.animated && !this.loopRunning && this.rate > 0) {
      this.loopRunning = true;
      this.animationLoop();
    }

    // Only apply clipping (making part of the heart transparent) when the atom is volume.
    // Also do not apply when data is needed
    if (this.atom && this.usingVolumeAtom && !this.extraData.needData) {
      const y = this.calculateY(this.atom.level);
      this.clipRect.y(y);
    }
  }

  async animationLoop() {
    if (this.rate === 0) {
      this.loopRunning = false;
      return;
    }
    const paddingTime = this.rate - MAXIMUM_HEART_RATE_ANIMATION_TIME_MS;
    // Divide rate by 2 to make both halves of the animation add together
    //(otherwise) animation is half speed
    const heartRateAnimationTime =
      paddingTime < 0
        ? this.rate / 2
        : MAXIMUM_HEART_RATE_ANIMATION_TIME_MS / 2;

    const heartPromise = new Promise(resolve => {
      this.animations.heartAnimationGroup = this.heartAnimationGroup
        .animate(heartRateAnimationTime, ">")
        .scale(ANIMATION_SCALE_END, ANIMATION_SCALE_END)
        .animate(heartRateAnimationTime, ">")
        .scale(ANIMATION_SCALE_START, ANIMATION_SCALE_START)
        .after(resolve);
    });

    const heartClipVenousLevelPromise = new Promise(resolve => {
      this.animations.heartClipVenousLevel = this.heartClipVenousLevel
        .animate(heartRateAnimationTime, ">")
        .scale(ANIMATION_SCALE_END, ANIMATION_SCALE_END)
        .animate(heartRateAnimationTime, ">")
        .scale(ANIMATION_SCALE_START, ANIMATION_SCALE_START)
        .after(resolve);
    });

    const heartClipBpLevelPromise = new Promise(resolve => {
      this.animations.heartClipBpLevel = this.heartClipBpLevel
        .animate(heartRateAnimationTime, ">")
        .scale(ANIMATION_SCALE_END, ANIMATION_SCALE_END)
        .animate(heartRateAnimationTime, ">")
        .scale(ANIMATION_SCALE_START, ANIMATION_SCALE_START)
        .after(resolve);
    });

    const bpPromise = new Promise(resolve => {
      this.animations.bpLevel = this.bpLevel
        .animate(heartRateAnimationTime, ">")
        .attr({ "stroke-width": BP_ANIMATION_STROKE_START })
        .animate(heartRateAnimationTime, ">")
        .attr({ "stroke-width": BP_ANIMATION_STROKE_END })
        .after(resolve);
    });

    const venousPromise = new Promise(resolve => {
      this.animations.venousLevel = this.venousLevel
        .animate(heartRateAnimationTime, ">")
        .attr({ "stroke-width": VENOUS_ANIMATION_STROKE_END })
        .animate(heartRateAnimationTime, ">")
        .attr({ "stroke-width": VENOUS_ANIMATION_STROKE_START })
        .after(resolve);
    });

    // synchronize all animations so next loop is never started without them completing first
    await Promise.all([
      heartPromise,
      heartClipVenousLevelPromise,
      heartClipBpLevelPromise,
      bpPromise,
      venousPromise
    ]);

    // If heart beat rate is slower than MAXIMUM_HEART_RATE_ANIMATION_TIME, wait
    // some time before starting the next animation loop. This prevents impossibly slow
    // heart beat animations taking multiple seconds
    await new Promise(resolve => {
      setTimeout(resolve, paddingTime > 0 ? paddingTime : 0);
    });

    if (!this.cleaningUp) {
      this.animationLoop();
    }
  }

  private createClippingPath() {
    const levelGroup = SVG.select(
      "#heartVolumeGroup",
      this.container.node
    ).first() as SVG.Container;
    this.clipRect = levelGroup.rect(300, 150).move(200, MIN_OFFSET);
    const clipContainer = this.container.clip();
    clipContainer.add(this.clipRect);
    levelGroup.clipWith(clipContainer);
  }

  private calculateY(volume: number) {
    if (volume === 0) {
      // Do not put any transparency up when there is no data
      return MIN_OFFSET;
    }
    return MIN_OFFSET + 25 * (5 - volume);
  }
}
